import { getColor as getColorv2 } from '@local/finsera-core/src/helpers';
import { _, am5, am5xy } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { FONT_FAMILY, FONT_SIZE } from '../../../../theme/layout-size';

const EMPTY_ARRAY: any[] = [];

export const createSeries = ({
  root,
  chart,
  data,
  category,
  xAxisRenderer,
  yAxisRenderer,
  tooltipText = '[bold]{name}:[/] {valueY}',
  hideSeries = EMPTY_ARRAY,
  seriesOrder = (c: string[]) => c,
  forceColor = {},
  rotation = undefined,
  displayNumbers = false,
  stacked = false,
  negativeColor = undefined,
  positiveColor = undefined,
  horizontal = false,
  colorByBar = false,
  paletteVersion = 'v2',
  count = undefined,
  hideGridLines = false,
}: {
  root: am5.Root;
  chart: am5xy.XYChart;
  data: types.widgets.TableData;
  category: string;
  xAxisRenderer: am5xy.CategoryAxis<am5xy.AxisRenderer>;
  yAxisRenderer: am5xy.ValueAxis<am5xy.AxisRenderer>;
  tooltipText: string;
  hideSeries: string[];
  seriesOrder: (_c: string[]) => string[];
  forceColor: Record<string, string>;
  rotation: number;
  displayNumbers: boolean;
  stacked: boolean;
  negativeColor: string;
  positiveColor: string;
  horizontal: boolean;
  colorByBar: boolean;
  paletteVersion: 'v1' | 'v2';
  count: types.widgets.CountData;
  hideGridLines: boolean;
}) => {
  const getColor = paletteVersion === 'v1' ? getColorv2 : getColorv2;

  const xRenderer = xAxisRenderer.get('renderer') as am5xy.AxisRendererX;
  xRenderer.labels.template.setAll({
    fontSize: FONT_SIZE.xxs,
    fontFamily: FONT_FAMILY,
  });

  const yRenderer = yAxisRenderer.get('renderer') as am5xy.AxisRendererY;

  yRenderer.labels.template.setAll({
    fontSize: FONT_SIZE.xxs,
    fontFamily: FONT_FAMILY,
  });

  if (!horizontal) {
    xRenderer.labels.template.setAll({
      rotation,
      centerY: rotation ? am5.p100 : am5.p50,
      centerX: am5.p50,
      paddingTop: 0,
      paddingBottom: 0,
    });

    if (!hideGridLines) {
      xAxisRenderer.get('renderer').grid.template.setAll({
        visible: false,
      });
    }
  } else {
    yRenderer.labels.template.setAll({
      rotation,
      centerY: rotation ? am5.p100 : am5.p50,
      centerX: am5.p50,
      paddingTop: 0,
      paddingBottom: 0,
      paddingRight: horizontal ? 15 : 0,
    });

    yAxisRenderer.get('renderer').grid.template.setAll({
      visible: true,
      strokeOpacity: 0.1,
      strokeWidth: 1,
    });
  }

  const valueField = horizontal ? 'valueX' : 'valueY';

  const createSingleSeries = (value: string, name: string, color: number) => {
    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        stacked,
        sequencedInterpolation: stacked,
        name: name,
        xAxis: xAxisRenderer,
        yAxis: yAxisRenderer,
        ...(horizontal
          ? { valueXField: value, categoryYField: category }
          : { valueYField: value, categoryXField: category }),
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: horizontal ? 'vertical' : 'horizontal',
          labelText: tooltipText,
        }),
      })
    );

    if (!_.isEmpty(count)) {
      series.columns.template.events.on('pointerover', function (ev) {
        const bucket = name;
        // Get the categoryX value from the dataItem's dataContext
        const dataItem = ev.target.dataItem;
        if (dataItem && dataItem.dataContext) {
          const dataContext = dataItem.dataContext as Record<string, any>;
          const history = dataContext[category];

          const tooltip = series.get('tooltip');
          if (tooltip && history && count && count[history] && typeof count[history] === 'object') {
            tooltip.set('labelText', `${tooltipText}\n[bold]Count:[/] ${count[history][bucket] || 0}`);
          }
        }
      });

      // Reset tooltip text when pointer leaves
      series.columns.template.events.on('pointerout', function () {
        const tooltip = series.get('tooltip');
        if (tooltip) {
          tooltip.set('labelText', tooltipText);
        }
      });
    }
    series.columns.template.setAll({
      stroke: am5.color(0xffffff),
      strokeWidth: 0,
      strokeOpacity: 0,
      fillOpacity: 1,
      ...(horizontal
        ? {
            cornerRadiusBR: 2,
            cornerRadiusTR: 2,
            height: am5.percent(90), // Optional: full height of the cell
          }
        : {
            cornerRadiusTL: 2,
            cornerRadiusTR: 2,
            // width: am5.p100, // Optional: full width of the cell
            width: am5.percent(80), // Default bar width (optional tweak)
          }),
    });

    const resolvedSeriesColor = am5.color(forceColor[name] || getColor(color));

    // Set color based on value, colorbybar or default to resolved color by series.
    series.columns.template.adapters.add('fill', (fill, target) => {
      const dataItem = target.dataItem;
      if (dataItem) {
        const value = dataItem.get(valueField as any);
        const index = series.dataItems.findIndex((item) => item.dataContext === dataItem.dataContext);
        if (colorByBar) return am5.color(getColor(index));
        if (value < 0 && negativeColor) return am5.color(negativeColor);
        if (value >= 0 && positiveColor) return am5.color(positiveColor);
        return resolvedSeriesColor;
      }
      return fill;
    });

    series.setAll({
      fill: resolvedSeriesColor,
      stroke: resolvedSeriesColor,
    });

    if (displayNumbers) {
      series.bullets.push(function (root, series, dataItem) {
        const value = dataItem.get(valueField);

        // Position labels above the bar for positive values and below for negative values
        const dy = value >= 0 ? -28 : 0; // -28 for above, 28 for below

        return am5.Bullet.new(root, {
          ...(horizontal ? { locationX: value >= 0 ? 1 : 0 } : { locationY: 1 }),
          sprite: am5.Label.new(root, {
            text: `{${valueField}}`,
            populateText: true,
            ...(!horizontal ? { dy, centerX: am5.p50 } : { centerY: am5.p50 }),
            fontSize: 11,
            fontFamily: FONT_FAMILY,
            fill: root.interfaceColors.get('text'),
          }),
        });
      });
    }

    series.get('tooltip')?.label.setAll({
      fontSize: FONT_SIZE.xxs,
      fontFamily: FONT_FAMILY,
    });

    return series;
  };

  const firstObj = data?.[0] || {};
  const series = Object.keys(_.omit(firstObj, [category])).filter((el) => el);

  let hidden = 0;
  seriesOrder(series).forEach((seriesKey, index) => {
    if (!hideSeries.includes(seriesKey)) {
      const seriesObj = createSingleSeries(seriesKey, seriesKey, index - hidden);
      seriesObj.data.setAll(data);
    } else {
      hidden += 1;
    }
  });
};
